/* AddEntryForm.jsx */
import React, { useState, useEffect } from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Switch,
  FormControlLabel,
  Autocomplete,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { styled } from '@mui/system';
import { toast } from 'react-toastify';

const StyledPopper = styled('div')(({ theme }) => ({
  zIndex: 13000,
  '& .MuiPaper-root': {
    borderRadius: '8px',
    background: '#FFF',
    boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.32)',
  },
}));

const StyledListItem = styled('li')({
  '&:hover': { backgroundColor: '#F5F5F6', cursor: 'pointer' },
});

const AddEntryForm = ({
  keyType,
  setKeyType,
  selectedSortCode,
  setSelectedSortCode,
  manualKey,
  setManualKey,
  value,
  setValue,
  isAmountPercentage,
  setIsAmountPercentage,
  sortCodeOptions,
  inputPropsStyle,
  loading,
  onAdd,
  onCancel,
  t,
  showSaveButton = false,
  onSave,
  // New props for percentage calculation
  onGetSortCodeTotal,
  client,
  year,
  token
}) => {
  const [sortCodeTotal, setSortCodeTotal] = useState(0);
  const [percentage, setPercentage] = useState('');
  const [calculatedValue, setCalculatedValue] = useState('');
  const [loadingSortCodeTotal, setLoadingSortCodeTotal] = useState(false);


  
  // Fetch SortCode total when SortCode is selected
  useEffect(() => {
    const fetchSortCodeTotal = async () => {
      if (keyType === 'sortcode' && selectedSortCode && onGetSortCodeTotal && client && year && token) {
        setLoadingSortCodeTotal(true);
        try {
          const data = await onGetSortCodeTotal(client.caseId, client.accountantId, year, selectedSortCode, token);
          if (data && data.sortCodeTotal !== undefined) {
            const total = data.sortCodeTotal || 0;
            setSortCodeTotal(total);
          }
        } catch (error) {
          console.error('Error fetching sort code total:', error);
          setSortCodeTotal(0);
        } finally {
          setLoadingSortCodeTotal(false);
        }
      } else {
        setSortCodeTotal(0);
      }
    };

    fetchSortCodeTotal();
  }, [keyType, selectedSortCode, onGetSortCodeTotal, client, year, token]);

  // Calculate value from percentage when percentage changes
  useEffect(() => {
    // Allow negative totals - change condition from > 0 to !== 0
    if (!isAmountPercentage && sortCodeTotal !== 0) {
      if (percentage && percentage.trim() !== '') {
        const percentageNum = parseFloat(percentage);
        if (!isNaN(percentageNum) && percentageNum > 0) {
          // Calculate: percentage × total (works with both positive and negative totals)
          const calculated = (percentageNum / 100) * sortCodeTotal;
          const calculatedStr = calculated.toFixed(2);
          setCalculatedValue(calculatedStr);
          setValue(calculatedStr);
        }
      } else {
        // When percentage is cleared/empty, reset calculated value to the original total
        const totalStr = sortCodeTotal.toFixed(2);
        setCalculatedValue(totalStr);
        setValue(totalStr);
      }
    } else if (isAmountPercentage) {
      // Don't clear percentage when switching to amount mode - preserve it
      // Only clear calculated value
      setCalculatedValue('');
    }
  }, [percentage, sortCodeTotal, isAmountPercentage, setValue]);

  const handleKeyTypeChange = (newKeyType) => {
    setKeyType(newKeyType);
    setSelectedSortCode('');
    setManualKey('');
    setSortCodeTotal(0);
    setPercentage('');
    setCalculatedValue('');
  };

  const handlePercentageChange = (newPercentage) => {
    setPercentage(newPercentage);
  };

  const handleValueChange = (newValue) => {
    setValue(newValue);
    if (isAmountPercentage) {
      setCalculatedValue('');
      setPercentage('');
    }
  };

  const isValid = (() => {
    if (isAmountPercentage) {
      // For amount mode, just check if value and key are set
      return value && (keyType === 'sortcode' ? selectedSortCode : manualKey);
    } else {
      // For percentage mode, check if percentage is set and calculated value exists
      return percentage && calculatedValue && (keyType === 'sortcode' ? selectedSortCode : manualKey);
    }
  })();

  // Debug: Log current state
  const shouldShowTwoInputs = !isAmountPercentage && keyType === 'sortcode' && selectedSortCode;
  console.log('AddEntryForm state:', {
    isAmountPercentage,
    keyType,
    selectedSortCode,
    shouldShowTwoInputs,
    percentage,
    sortCodeTotal,
    calculatedValue
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8, padding: '8px', border: '1px solid #304FFF', borderRadius: 4, backgroundColor: '#f5f5f5' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {/* Key Type Selector */}
        <FormControl size="small" style={{ minWidth: 120 }}>
          <InputLabel>{t('KeyType') || 'סוג מפתח'}</InputLabel>
          <Select
            value={keyType}
            onChange={(e) => handleKeyTypeChange(e.target.value)}
            label={t('KeyType') || 'סוג מפתח'}
            MenuProps={{
              disablePortal: false,
              style: { zIndex: 13000 },
              PaperProps: {
                style: { zIndex: 13000 },
              },
            }}
          >
            <MenuItem value="sortcode">{t('SortCode') || 'קוד מיון'}</MenuItem>
            <MenuItem value="manual">{t('ManualText') || 'טקסט ידני'}</MenuItem>
          </Select>
        </FormControl>
        
        {/* Key Input */}
        <div style={{ flex: 1 }}>
          {keyType === 'sortcode' ? (
            <Autocomplete
              options={sortCodeOptions}
              getOptionLabel={(option) => `${option.sortCodeId} - ${option.sortCodeName}`}
              value={sortCodeOptions.find(opt => opt.sortCodeId === selectedSortCode) || null}
              onChange={(event, newValue) => {
                const newKey = newValue ? newValue.sortCodeId : '';
                const currentKey = selectedSortCode;
                
                // Only reset if the key actually changed (not just clearing)
                if (newKey !== currentKey) {
                  setSelectedSortCode(newKey);
                  // Reset percentage and calculated value when sort code changes
                  setPercentage('');
                  setCalculatedValue('');
                  // Reset the total to 0 so it will be fetched fresh
                  setSortCodeTotal(0);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('SortCode') || 'קוד מיון'}
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    ...inputPropsStyle,
                  }}
                  inputProps={{
                    ...params.inputProps,
                    style: { textAlign: 'right', direction: 'rtl' },
                  }}
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <Tooltip title={option.isEditable ? t('RightClickToEdit') || 'Right click to edit' : t('NotEditable') || 'Not editable'} placement="top">
                  <StyledListItem {...props}>
                    {`${option.sortCodeId} - ${option.sortCodeName}`}
                  </StyledListItem>
                </Tooltip>
              )}
              PopperComponent={(props) => (
                <StyledPopper {...props} style={{ ...props.style, direction: 'rtl', zIndex: 13000 }} />
              )}
            />
          ) : (
            <TextField
              size="small"
              fullWidth
              placeholder={t('ManualKey') || 'מפתח ידני'}
              value={manualKey}
              onChange={(e) => setManualKey(e.target.value)}
            />
          )}
        </div>
        
        {/* Value Type Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={isAmountPercentage}
              onChange={(e) => {
                const newMode = e.target.checked;
                setIsAmountPercentage(newMode);
                // Don't clear percentage when switching to amount mode - preserve it
                // When switching to Amount mode, set value to the calculated value if available
                if (newMode) {
                  // If we have a calculated value, use it; otherwise use the current value
                  if (calculatedValue) {
                    setValue(calculatedValue);
                  }
                  // If no calculated value but we have a sort code total, use that
                  else if (keyType === 'sortcode' && selectedSortCode && sortCodeTotal !== 0) {
                    const totalStr = sortCodeTotal.toFixed(2);
                    setValue(totalStr);
                  }
                  // Only clear calculated value, not percentage
                  setCalculatedValue('');
                }
                // When switching back to percentage mode, restore calculated value if percentage exists
                else {
                  // If we have a percentage, recalculate the value from it
                  if (percentage) {
                    const percentageNum = parseFloat(percentage);
                    if (!isNaN(percentageNum) && percentageNum > 0 && sortCodeTotal !== 0) {
                      const calculated = (percentageNum / 100) * sortCodeTotal;
                      const calculatedStr = calculated.toFixed(2);
                      setCalculatedValue(calculatedStr);
                      setValue(calculatedStr);
                    }
                  }
                  // If no percentage but we have current value and total, calculate percentage from current value
                  else if (value && keyType === 'sortcode' && selectedSortCode && sortCodeTotal !== 0) {
                    const valueNum = parseFloat(value);
                    // Allow both positive and negative values for calculation
                    if (!isNaN(valueNum) && valueNum !== 0) {
                      const calculatedPercentage = (valueNum / sortCodeTotal) * 100;
                      setPercentage(calculatedPercentage.toFixed(2));
                      setCalculatedValue(value);
                    }
                  }
                  // If no value but we have total, set calculated value to total
                  else if (keyType === 'sortcode' && selectedSortCode && sortCodeTotal !== 0) {
                    const totalStr = sortCodeTotal.toFixed(2);
                    setCalculatedValue(totalStr);
                    setValue(totalStr);
                  }
                  // Otherwise, just set calculated value to current value if it exists
                  else if (value) {
                    setCalculatedValue(value);
                  }
                }
              }}
              size="small"
            />
          }
          label={isAmountPercentage ? (t('Amount') || 'סכום') : (t('Percentage') || 'אחוז')}
          style={{ margin: 0, whiteSpace: 'nowrap' }}
        />
        
        {/* Value Inputs */}
        {!isAmountPercentage && keyType === 'sortcode' && selectedSortCode ? (
          // Percentage mode with SortCode: Show two inputs (percentage and calculated value)
          <>
            <div style={{ flex: 1, minWidth: '120px' }}>
              <TextField
                size="small"
                fullWidth
                type="number"
                placeholder={t('Percentage') || 'אחוז'}
                value={percentage}
                onChange={(e) => {
                  const newPercentage = e.target.value;
                  // Allow empty string for clearing
                  if (newPercentage === '') {
                    handlePercentageChange('');
                    return;
                  }
                  // Validate percentage range
                  const percentageNum = parseFloat(newPercentage);
                  if (!isNaN(percentageNum)) {
                    if (percentageNum < 0) {
                      toast.error(t('PercentageMustBeGreaterThanZero') || 'האחוז חייב להיות גדול או שווה ל-0');
                      handlePercentageChange('');
                      return;
                    }
                    if (percentageNum > 100) {
                      toast.error(t('PercentageMustBeLessThanOrEqualToOneHundred') || 'האחוז חייב להיות קטן או שווה ל-100');
                      handlePercentageChange('');
                      return;
                    }
                  }
                  handlePercentageChange(newPercentage);
                }}
                helperText={loadingSortCodeTotal ? (t('Loading') || 'טוען...') : sortCodeTotal > 0 ? `${t('Total') || 'סה"כ'}: ${sortCodeTotal.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : (t('SelectSortCode') || 'בחר קוד מיון')}
              />
            </div>
            <div style={{ flex: 1, minWidth: '120px' }}>
              <TextField
                size="small"
                fullWidth
                type="number"
                placeholder={t('CalculatedValue') || 'ערך מחושב'}
                value={calculatedValue}
                disabled
                InputProps={{
                  readOnly: true,
                }}
                helperText={calculatedValue && percentage ? `${percentage}% × ${sortCodeTotal.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} = ${calculatedValue}` : (t('EnterPercentage') || 'הכנס אחוז')}
              />
            </div>
          </>
        ) : (
          // Amount mode or manual key: Show single input
          <div style={{ flex: 1 }}>
            <TextField
              size="small"
              fullWidth
              type="number"
              placeholder={isAmountPercentage ? (t('Amount') || 'סכום') : (t('Percentage') || 'אחוז')}
              value={value}
              onChange={(e) => handleValueChange(e.target.value)}
            />
          </div>
        )}
        
        {/* Action Button */}
        {showSaveButton ? (
          <>
            <IconButton 
              size="small" 
              onClick={onSave}
              disabled={loading || !isValid}
              sx={{ color: '#304FFF' }}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={onCancel}
              sx={{ color: 'red' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <>
            <IconButton 
              size="small" 
              onClick={() => {
                // Pass percentage if in percentage mode with SortCode
                if (!isAmountPercentage && keyType === 'sortcode' && selectedSortCode && percentage) {
                  onAdd(parseFloat(percentage));
                } else {
                  onAdd(null);
                }
              }}
              disabled={loading || !isValid}
              sx={{ color: '#304FFF' }}
            >
              <AddIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={onCancel}
              sx={{ color: 'red' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </div>
    </div>
  );
};

export default AddEntryForm;

